import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { Images } from "../../../utils/Images";
import ImageSlider from "../../../components/common/CampaignDetailPageComponents/ImageSlider";

const sanitizeUrl = (url) => String(url || "").replace(/%22$/i, "").replace(/"+$/, "");
const hasUpdateText = (title, description) => {
  const t = String(title || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const d = String(description || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return Boolean(t || d);
};
const formatDate = (value) => {
  if (!value) return "--/--/----";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "--/--/----";
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${day}/${month}/${year}`;
};

const CampaignUpdatesSection = ({ vendor }) => {
  const isMobile = useMediaQuery("(max-width: 599.95px)");
  const isTablet = useMediaQuery("(min-width:600px) and (max-width:899.95px)");
  const isLaptop = useMediaQuery("(min-width:900px) and (max-width:1199.95px)");
  const isDesktop = useMediaQuery("(min-width: 1200px)");
  const isAbove1200 = useMediaQuery("(min-width:1201px)");
  const is1200AndBelow = useMediaQuery("(max-width:1200px)");
  const isDesktop1420OrBelow = useMediaQuery("(min-width:1200px) and (max-width:1420px)");
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const mobileImagesRef = useRef(null);
  const hasVendorUpdateDetails = Array.isArray(vendor?.updates?.updatedetails);
  const apiUpdates = hasVendorUpdateDetails
    ? vendor.updates.updatedetails
        .filter((item) => item && (item.status === 1 || item.status === true))
        .sort((a, b) => {
          const aTime = new Date(a?.createdAt || 0).getTime();
          const bTime = new Date(b?.createdAt || 0).getTime();
          return bTime - aTime;
        })
        .map((item) => ({
          id: item.id,
          date: formatDate(item.createdAt),
          title: item.title || "",
          description: item.description || "",
        }))
        .filter((item) => hasUpdateText(item.title, item.description))
        .slice(0, 3)
    : [];

  const updates = apiUpdates;
  const updateImageGroups = Array.isArray(vendor?.updates?.updateimages) ? vendor.updates.updateimages : [];
  const normalizedUpdateImages = updateImageGroups
    .flatMap((group) => (Array.isArray(group) ? group : []))
    .map((item) => sanitizeUrl(item?.url))
    .filter(Boolean);
  const updateImages = normalizedUpdateImages;

  const hasCampaignUpdatesContent = updateImages.length > 0 || updates.length > 0;
  if (!hasCampaignUpdatesContent) {
    return null;
  }
  const mobileStyleCardWidth = isMobile ? 202.8 : 650;
  const mobileStyleCardHeight = isMobile ? 139 : 366;
  const mobileStyleCardGap = isMobile ? 13.765 : 16;

  useEffect(() => {
    if (activeImageIndex >= updateImages.length) {
      setActiveImageIndex(0);
    }
  }, [activeImageIndex, updateImages.length]);

  const handleMobileImageScroll = () => {
    const container = mobileImagesRef.current;
    if (!container || updateImages.length === 0) return;

    const cardWidth = mobileStyleCardWidth;
    const cardGap = mobileStyleCardGap;
    const step = cardWidth + cardGap;
    const nextIndex = Math.round(container.scrollLeft / step);
    const boundedIndex = Math.max(0, Math.min(updateImages.length - 1, nextIndex));
    if (boundedIndex !== activeImageIndex) {
      setActiveImageIndex(boundedIndex);
    }
  };

  return (
    <Box sx={{ width: isDesktop ? "94.44%" : "100%", maxWidth: "1440px", mx: "auto", px: isDesktop ? 0 : "16px", mb: isMobile ? "24px" : "90px" }}>
      <Box
        sx={{
          background: isMobile ? "#F5F5F5" : "#FFFFFF",
          border: isMobile ? "0.67px solid #EAEAF0" : "none",
          borderRadius: isMobile ? "16px" : "16px",
          boxShadow: isMobile ? "0px 1.341px 10.727px rgba(0, 0, 0, 0.06)" : "0px -7px 50px rgba(0,0,0,0.04), 0px 10px 26px rgba(0,0,0,0.04)",
          p: isDesktop ? "40px" : isTablet ? "24px 20px" : "19px 15px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {isAbove1200 && updates.length > 0 && (
          <>
            <Box
              component="img"
              src={Images.stringHand}
              alt="decoration"
              sx={{
                position: "absolute",
                right: isDesktop ? "58px" : "28px",
                top: isDesktop ? "64px" : "44px",
                width: isDesktop ? "188px" : "140px",
                opacity: 0.95,
                transform: "scaleX(-1)",
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <Box
              component="img"
              src={Images.heart}
              alt="heart"
              sx={{
                position: "absolute",
                left: isDesktop ? (isDesktop1420OrBelow ? "68.5%" : "56.5%") : "52%",
                top: isDesktop ? "86px" : "66px",
                width: isDesktop ? "24px" : "18px",
                zIndex: 2,
              }}
            />
          </>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "24px" : "30px", position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? "14px" : "20px",
              pr: isDesktop ? (updates.length > 0 ? "240px" : 0) : isLaptop ? (updates.length > 0 ? "150px" : 0) : 0,
              alignItems: isMobile ? "center" : "flex-start",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "10px" : "12px", height: "18.973px" }}>
              <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", background: "#8EA946" }} />
              <Typography sx={{ fontWeight: 400, fontSize: isMobile ? "10px" : "14px", lineHeight: isMobile ? 1 : "18.2px", letterSpacing: isMobile ? "2px" : "2.1px", textTransform: "uppercase", color: "#131313" }}>
                Campaign Updates
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 700, fontSize: isDesktop ? "36px" : isTablet || isLaptop ? "30px" : "18px", lineHeight: isMobile ? "29.124px" : "1.4", letterSpacing: "-0.36px", color: "#1D1D1B", maxWidth: isMobile ? "278px" : "760px", textAlign: isMobile ? "center" : "left" }}>
              See The Impact You&apos;re Creating In Real Time
            </Typography>

            <Typography sx={{ fontWeight: 400, fontSize: isMobile ? "12px" : "16px", lineHeight: isMobile ? "20px" : "25.888px", color: "#7D7D7D", maxWidth: isMobile ? "296px" : "1060px", textAlign: isMobile ? "center" : "left", letterSpacing: isMobile ? "-0.24px" : "normal" }}>
              Stay updated with real progress, key milestones, and how your support is making a meaningful difference every day.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: isDesktop ? "40px" : isLaptop ? "24px" : "20px",
              flexDirection: isDesktop && updateImages.length > 0 ? "row" : "column",
              width: "100%",
              maxWidth: "1280px",
              alignItems: updates.length === 0 ? "stretch" : "flex-start",
            }}
          >
            {updateImages.length > 0 ? (
            <Box sx={{ width: isDesktop ? "600px" : isLaptop ? "100%" : "100%", position: "relative", flexShrink: 0 }}>
              {is1200AndBelow ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", alignItems: "center", width: "100%" }}>
                  <Box
                    ref={mobileImagesRef}
                    onScroll={handleMobileImageScroll}
                    sx={{
                      width: "100%",
                      display: "flex",
                      gap: `${mobileStyleCardGap}px`,
                      overflowX: "auto",
                      overflowY: "hidden",
                      pb: "2px",
                      "&::-webkit-scrollbar": { display: "none" },
                      scrollbarWidth: "none",
                    }}
                  >
                    {updateImages.map((image, index) => (
                      <Box
                        key={`${image}-${index}`}
                        component="img"
                        src={image}
                        alt={`campaign update ${index + 1}`}
                        sx={{
                          width: `${mobileStyleCardWidth}px`,
                          minWidth: `${mobileStyleCardWidth}px`,
                          height: `${mobileStyleCardHeight}px`,
                          borderRadius: "6px",
                          objectFit: "cover",
                        }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px", height: "6px" }}>
                    {updateImages.map((_, index) => (
                      <Box key={index} sx={{ width: "6px", height: "6px", borderRadius: "50%", background: index === activeImageIndex ? "#8EA946" : "#D9D9D9" }} />
                    ))}
                  </Box>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      width: "100%",
                      height: isDesktop ? "493.333px" : isTablet ? "380px" : "320px",
                      borderRadius: "20px",
                      overflow: "hidden",
                      "& .slick-list": { borderRadius: "20px", overflow: "hidden" },
                      "& .slick-track": { display: "flex" },
                      "& .slick-slide > div": { height: isDesktop ? "493.333px" : isTablet ? "380px" : "320px" },
                      "& .slick-dots": { display: "none !important" },
                    }}
                  >
                    <ImageSlider
                      images={updateImages}
                      settings={{
                        dots: false,
                        infinite: true,
                        speed: 700,
                        slidesToShow: 1,
                        slidesToScroll: 1,
                        autoplay: true,
                        autoplaySpeed: 2600,
                        arrows: false,
                        beforeChange: (_, next) => setActiveImageIndex(next),
                      }}
                      imgSx={{
                        width: "100%",
                        height: isDesktop ? "493.333px" : isTablet ? "380px" : "320px",
                        objectFit: "cover",
                        borderRadius: 0,
                      }}
                      noPaddingRight={true}
                    />
                  </Box>

                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "18px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      height: "32px",
                      borderRadius: "40px",
                      px: "13.333px",
                      py: "8px",
                      background: "rgba(255,255,255,0.10)",
                      backdropFilter: "blur(66.667px)",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {updateImages.map((_, index) => (
                      <Box key={index} sx={{ width: "8px", height: "8px", borderRadius: "50%", background: index === activeImageIndex ? "#A9C74E" : "#FFFFFF" }} />
                    ))}
                  </Box>
                </>
              )}
            </Box>
            ) : null}

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: updates.length === 0 ? 0 : isMobile ? "12px" : "20px",
                justifyContent: updates.length === 0 ? "center" : "center",
                alignItems: updates.length === 0 ? "center" : "stretch",
                alignSelf: updates.length === 0 ? "stretch" : undefined,
                width: "100%",
                minWidth: 0,
                maxWidth: updateImages.length === 0 ? "100%" : undefined,
              }}
            >
              {updates.length === 0 ? (
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? "14px" : "16px",
                    lineHeight: isMobile ? "22px" : "24px",
                    color: "#7D7D7D",
                    textAlign: "center",
                    width: "100%",
                    px: isMobile ? "8px" : 0,
                  }}
                >
                  No updates yet
                </Typography>
              ) : (
                updates.map((item, index) => (
                  <Box key={item.id} sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "24px" }}>
                    {isMobile ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "10px", lineHeight: "12px", color: "#000000" }}>#{index + 1} -</Typography>
                        <Box sx={{ width: "1px", height: "8px", background: "#B9B9B8", transform: "rotate(90deg)" }} />
                        <Typography sx={{ fontWeight: 500, fontSize: "10px", lineHeight: "12px", color: "#151513" }}>
                          Updated on <Box component="span" sx={{ fontWeight: 800, color: "#8EA946" }}>{item.date}</Box>
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          minHeight: "47px",
                          borderRadius: "8px",
                          background: "#F5F8ED",
                          display: "flex",
                          alignItems: "center",
                          px: "16px",
                          gap: "6px",
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, fontSize: "14px", lineHeight: "14.4px", color: "#000000" }}>#{index + 1} -</Typography>
                        <Box sx={{ width: "1px", height: "12px", background: "#B9B9B8" }} />
                        <Typography sx={{ fontWeight: 500, fontSize: "14px", lineHeight: "14.4px", color: "#151513" }}>
                          <Box component="span" sx={{ fontWeight: 700 }}>Updated</Box> on{" "}
                          <Box component="span" sx={{ fontWeight: 800, color: "#8EA946" }}>{item.date}</Box>
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "14px" : "16px", width: isMobile ? "296px" : "100%" }}>
                      <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "14px" : "14px", lineHeight: isMobile ? "14px" : "14px", color: "#000000" }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? "19.416px" : "22.652px", color: "#4A4A49" }}>
                        {item.description}
                      </Typography>
                    </Box>

                    {isMobile && index < updates.length - 1 && <Box sx={{ width: "100%", height: "1px", background: "#D9D9D9" }} />}
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CampaignUpdatesSection;
